/**
 * qrcode.js — Pure-JS QR Code generator (no external dependencies)
 * Produces standard-compliant QR Version 1–5, error correction level M.
 * Output: 2D boolean matrix (true = dark module) that can be drawn to
 * canvas, rendered as SVG, or embedded in HTML.
 *
 * Usage:
 *   import { generateQRMatrix, qrToSVG, qrToCanvas } from './qrcode';
 *   const matrix = generateQRMatrix('https://example.com/gate-scan/abc123');
 *   const svg    = qrToSVG(matrix, { size: 200, fg: '#000', bg: '#fff' });
 */

// ── Galois Field GF(256) ──────────────────────────────────────────────────────
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
})();

const gfMul = (a, b) => (a && b) ? GF_EXP[(GF_LOG[a] + GF_LOG[b]) % 255] : 0;

function rsGenPoly(n) {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const ng = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      ng[j]     ^= gfMul(g[j], GF_EXP[i]);
      ng[j + 1] ^= g[j];
    }
    g = ng;
  }
  return g;
}

function rsEncode(data, ecCount) {
  const gen = rsGenPoly(ecCount);
  const msg = [...data, ...new Array(ecCount).fill(0)];
  for (let i = 0; i < data.length; i++) {
    const c = msg[i];
    if (c) for (let j = 1; j < gen.length; j++) msg[i + j] ^= gfMul(gen[j], c);
  }
  return msg.slice(data.length);
}

// ── QR Version tables (error correction level M) ──────────────────────────────
// [dataCW, ecCW, totalCW, modules, alignmentPositions]
const VERSIONS = [
  null,
  { d: 16, ec: 10, total: 26,  N: 21, al: []         },  // v1
  { d: 28, ec: 16, total: 44,  N: 25, al: [18]        },  // v2
  { d: 44, ec: 26, total: 70,  N: 29, al: [22]        },  // v3
  { d: 64, ec: 36, total: 100, N: 33, al: [26]        },  // v4
  { d: 86, ec: 48, total: 134, N: 37, al: [30]        },  // v5
];

// Format info strings for M level, masks 0–7 (pre-computed with BCH)
// Mask 0: (r+c) % 2 === 0
const FORMAT_M0 = [1,0,1,0,1,0,0,0,0,0,1,0,0,1,0]; // M-level, mask 0

function pickVersion(byteLen) {
  for (let v = 1; v <= 5; v++) {
    if (byteLen + 4 <= VERSIONS[v].d) return v; // +4 = mode + char-count indicators
  }
  throw new Error(`QR: text too long (${byteLen} bytes). Max ~82 bytes.`);
}

/**
 * Generate a QR code matrix for the given text.
 * Returns a 2D array of 0s (light) and 1s (dark).
 */
export function generateQRMatrix(text) {
  // Encode to UTF-8 bytes
  const bytes = Array.from(new TextEncoder().encode(text));
  const ver   = pickVersion(bytes.length);
  const spec  = VERSIONS[ver];
  const N     = spec.N;

  // ── Build data bitstream ──────────────────────────────────────────────────
  const bits = [];
  const push = (val, len) => { for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1); };

  push(0b0100, 4);         // byte mode indicator
  push(bytes.length, 8);   // character count (8 bits for versions 1-9)
  bytes.forEach(b => push(b, 8));
  push(0, 4);              // terminator (up to 4 zeros)
  while (bits.length % 8) bits.push(0);

  // Padding codewords
  const PADS = [0xEC, 0x11];
  for (let i = 0; bits.length < spec.d * 8; i++) push(PADS[i % 2], 8);

  // Convert bits → bytes → add EC
  const dataCW = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0; for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    dataCW.push(b);
  }
  const ecCW  = rsEncode(dataCW, spec.ec);
  const allCW = [...dataCW, ...ecCW];

  // Convert codewords → flat bit array
  const allBits = [];
  allCW.forEach(cw => { for (let i = 7; i >= 0; i--) allBits.push((cw >> i) & 1); });

  // ── Build module matrix ───────────────────────────────────────────────────
  const mat  = Array.from({ length: N }, () => new Array(N).fill(0));
  const func = Array.from({ length: N }, () => new Array(N).fill(false)); // functional (reserved) modules

  const set = (r, c, v) => {
    if (r >= 0 && r < N && c >= 0 && c < N) { mat[r][c] = v; func[r][c] = true; }
  };

  // Finder pattern (7x7 with 1-cell separator border)
  const finder = (row, col) => {
    for (let dr = -1; dr <= 7; dr++) {
      for (let dc = -1; dc <= 7; dc++) {
        const inSquare   = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6;
        const onRim      = inSquare && (dr === 0 || dr === 6 || dc === 0 || dc === 6);
        const inCore     = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
        set(row + dr, col + dc, (onRim || inCore) ? 1 : 0);
      }
    }
  };
  finder(0, 0);
  finder(0, N - 7);
  finder(N - 7, 0);

  // Timing patterns
  for (let i = 8; i < N - 8; i++) {
    set(6, i, i % 2 === 0 ? 1 : 0);
    set(i, 6, i % 2 === 0 ? 1 : 0);
  }

  // Dark module (always dark)
  set(N - 8, 8, 1);

  // Alignment patterns (for versions ≥ 2)
  for (const ap of spec.al) {
    for (let dr = -2; dr <= 2; dr++) {
      for (let dc = -2; dc <= 2; dc++) {
        const onRim  = Math.abs(dr) === 2 || Math.abs(dc) === 2;
        const inCore = dr === 0 && dc === 0;
        set(ap + dr, ap + dc, (onRim || inCore) ? 1 : 0);
      }
    }
  }

  // Reserve format information areas (will be written after masking)
  for (let i = 0; i <= 8; i++) { set(8, i, 0); set(i, 8, 0); }
  for (let i = N - 8; i < N; i++) { set(8, i, 0); set(i, 8, 0); }

  // ── Place data modules (zigzag scan) ─────────────────────────────────────
  let bitIdx  = 0;
  let upward  = true;
  for (let right = N - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // skip vertical timing strip
    for (let pass = 0; pass < N; pass++) {
      const row = upward ? N - 1 - pass : pass;
      for (let dc = 0; dc < 2; dc++) {
        const col = right - dc;
        if (!func[row][col]) {
          mat[row][col] = bitIdx < allBits.length ? allBits[bitIdx++] : 0;
        }
      }
    }
    upward = !upward;
  }

  // ── Apply mask 0: (row + col) % 2 === 0 → flip ───────────────────────────
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (!func[r][c] && (r + c) % 2 === 0) mat[r][c] ^= 1;
    }
  }

  // ── Write format information (M level, mask 0) ────────────────────────────
  const fmt = FORMAT_M0;
  // Horizontal strip near top-left finder
  for (let i = 0; i <= 5; i++) { mat[8][i] = fmt[i]; func[8][i] = true; }
  mat[8][7] = fmt[6]; func[8][7] = true;
  mat[8][8] = fmt[7]; func[8][8] = true;
  mat[7][8] = fmt[8]; func[7][8] = true;
  // Vertical strip near top-left finder
  for (let i = 9; i <= 14; i++) { mat[14 - i][8] = fmt[i]; func[14 - i][8] = true; }
  // Bottom-left format copy
  for (let i = 0; i <= 6; i++) { mat[N - 1 - i][8] = fmt[i]; func[N - 1 - i][8] = true; }
  // Top-right format copy
  for (let i = 8; i <= 14; i++) { mat[8][N - 15 + i] = fmt[i]; func[8][N - 15 + i] = true; }

  return mat;
}

/**
 * Convert a QR matrix to an SVG string.
 * @param {number[][]} matrix
 * @param {{ size?: number, fg?: string, bg?: string, quiet?: number }} options
 */
export function qrToSVG(matrix, options = {}) {
  const { size = 200, fg = '#000000', bg = '#ffffff', quiet = 4 } = options;
  const N     = matrix.length;
  const total = N + quiet * 2;
  const cell  = size / total;

  let rects = `<rect width="${size}" height="${size}" fill="${bg}"/>`;
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (matrix[r][c]) {
        const x = (quiet + c) * cell;
        const y = (quiet + r) * cell;
        rects += `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${(cell + 0.1).toFixed(2)}" height="${(cell + 0.1).toFixed(2)}" fill="${fg}"/>`;
      }
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">${rects}</svg>`;
}

/**
 * Draw a QR matrix to a canvas element.
 * @param {number[][]} matrix
 * @param {HTMLCanvasElement} canvas
 * @param {{ fg?: string, bg?: string, quiet?: number }} options
 */
export function qrToCanvas(matrix, canvas, options = {}) {
  const { fg = '#000000', bg = '#ffffff', quiet = 4 } = options;
  const N    = matrix.length;
  const size = canvas.width;
  const cell = size / (N + quiet * 2);
  const ctx  = canvas.getContext('2d');

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, size, size);

  ctx.fillStyle = fg;
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (matrix[r][c]) {
        ctx.fillRect(
          Math.floor((quiet + c) * cell),
          Math.floor((quiet + r) * cell),
          Math.ceil(cell),
          Math.ceil(cell)
        );
      }
    }
  }
}

/**
 * Generate a standalone data-URL PNG from text.
 * @param {string} text
 * @param {number} size
 */
export function qrToDataURL(text, size = 200) {
  const matrix = generateQRMatrix(text);
  const canvas = document.createElement('canvas');
  canvas.width  = size;
  canvas.height = size;
  qrToCanvas(matrix, canvas);
  return canvas.toDataURL('image/png');
}
